import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { Package, Box, CheckCircle, X, AlertCircle, MapPin, Navigation } from 'lucide-react';

/**
 * BoxAllocationModal with Zone Detection
 * 
 * Modal for packers to allocate a packed order to a delivery box
 * Shows available boxes, detects delivery zone, and provides bay placement instructions
 */
const BoxAllocationModal = ({ order, packingTask, onClose, onAllocated }) => {
  const [boxes, setBoxes] = useState([]);
  const [selectedBox, setSelectedBox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allocating, setAllocating] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deliveryZone, setDeliveryZone] = useState(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [completedAllocation, setCompletedAllocation] = useState(null);

  useEffect(() => {
    checkExistingAllocation();
  }, []);

  // Check if order already has a box allocation (resuming confirmation)
  const checkExistingAllocation = async () => {
    if (order?.box_allocation_id) {
      console.log('📦 Order already has box allocation, loading existing...');
      
      const { data: allocation, error: allocError } = await supabase
        .from('box_allocations')
        .select('*, delivery_boxes(*)')
        .eq('id', order.box_allocation_id)
        .single();
      
      if (allocation && !allocError) {
        console.log('✅ Found existing allocation:', allocation);
        setCompletedAllocation(allocation);
        setSelectedBox(allocation.delivery_boxes);
        
        // Fetch full order details to get delivery zone
        const { data: fullOrder } = await supabase
          .from('orders')
          .select('delivery_zone, delivery_address_id, user_id')
          .eq('id', order.id)
          .single();
        
        if (fullOrder) {
          // Update the order object with zone info for detectZone
          order.delivery_zone = fullOrder.delivery_zone;
          order.delivery_address_id = fullOrder.delivery_address_id;
          order.user_id = fullOrder.user_id;
          
          // Manually detect zone with updated order info
          await detectZone();
        }
        
        setShowSuccessScreen(true);
        setLoading(false);
        return;
      }
    }
    
    // No existing allocation, load available boxes
    loadAvailableBoxes();
  };

  const detectZone = async () => {
    try {
      console.log('🔍 Detecting zone for order:', order.order_number);
      console.log('📦 Order object:', order);
      
      // Try multiple ways to get the zone
      let area = null;
      
      // Method 0: Check if order already has delivery_zone (fastest)
      if (order.delivery_zone) {
        area = order.delivery_zone;
        console.log('✅ Found area from order.delivery_zone:', area);
      }
      
      // Method 1: Check if order already has delivery_area
      else if (order.delivery_area) {
        area = order.delivery_area;
        console.log('✅ Found area from order.delivery_area:', area);
      }
      
      // Method 2: Try delivery_address_id
      else if (order.delivery_address_id) {
        const { data: addressData, error } = await supabase
          .from('addresses')
          .select('area')
          .eq('id', order.delivery_address_id)
          .single();

        if (error) {
          console.error('❌ Address query error:', error);
        } else {
          area = addressData?.area;
          console.log('✅ Found area from addresses table:', area);
        }
      }
      
      // Method 3: Try getting customer and their address (LAST RESORT - may get wrong address!)
      else if (order.user_id) {
        console.warn('⚠️ Falling back to user default address - may be incorrect for this order!');
        const { data: addressData, error } = await supabase
          .from('addresses')
          .select('area')
          .eq('user_id', order.user_id)
          .eq('is_default', true)
          .single();

        if (!error && addressData) {
          area = addressData.area;
          console.log('✅ Found area from customer default address:', area);
        }
      }
      
      // Method 4: Check if customer object has address with area
      if (!area && order.customer?.address?.area) {
        area = order.customer.address.area;
        console.log('✅ Found area from order.customer.address.area:', area);
      }

      if (!area) {
        console.error('❌ No area found! Setting default zone.');
        // Set a default zone so at least something displays
        setDeliveryZone({ 
          zone_name: 'Unknown Area', 
          zone_color: '#6B7280' 
        });
        return;
      }

      // Successfully found the area - use it as zone
      setDeliveryZone({ 
        zone_name: area, 
        zone_color: getZoneColor(area)
      });
      
      console.log('✅ Zone set successfully:', area);
    } catch (err) {
      console.error('❌ Error detecting zone:', err);
      // Set fallback zone
      setDeliveryZone({ 
        zone_name: 'Unknown Area', 
        zone_color: '#6B7280' 
      });
    }
  };

  const getZoneColor = (zoneName) => {
    const colors = {
      'Laudium': '#3B82F6',
      'Erasmia': '#10B981',
      'Wierda Park': '#F59E0B',
      'Eldoraigne': '#F59E0B',
      'Centurion': '#8B5CF6'
    };
    return colors[zoneName] || '#8B5CF6';
  };

  const loadAvailableBoxes = async () => {
    setLoading(true);
    try {
      const { data: boxesData, error: boxesError } = await supabase
        .from('delivery_boxes')
        .select(`
          id,
          box_number,
          status,
          last_used_at,
          current_zone,
          box_allocations (
            id,
            order_id,
            removed_at,
            orders!box_allocations_order_id_fkey (
              order_number,
              status
            )
          )
        `)
        .order('box_number', { ascending: true });

      if (boxesError) throw boxesError;

      const filteredBoxes = boxesData
        .map(box => {
          const activeAllocations = box.box_allocations?.filter(
            alloc => alloc.removed_at === null
          ) || [];
          
          return {
            ...box,
            activeOrders: activeAllocations.length,
            isAvailable: box.status === 'available' && activeAllocations.length === 0
          };
        })
        .filter(box => box.isAvailable);

      setBoxes(filteredBoxes);
      
      // Detect zone for new allocations
      await detectZone();
    } catch (err) {
      console.error('Error loading boxes:', err);
      setError('Failed to load delivery boxes');
    } finally {
      setLoading(false);
    }
  };

  const handleAllocateBox = async () => {
    if (!selectedBox) {
      setError('Please select a box');
      return;
    }

    setAllocating(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user found');
      }

      console.log('✅ Creating box allocation');
      
      const { data: allocation, error: allocError } = await supabase
        .from('box_allocations')
        .insert({
          box_id: selectedBox.id,
          order_id: order.id,
          packing_task_id: packingTask?.id,
          allocated_by: user.id
        })
        .select()
        .single();

      if (allocError) throw allocError;

      console.log('✅ Box allocation created:', allocation);
      console.log('📦 Now updating order');

      const { data: orderUpdateData, error: orderUpdateError } = await supabase
        .from('orders')
        .update({
          status: 'ready_for_dispatch',
          box_allocation_id: allocation.id
        })
        .eq('id', order.id)
        .select();

      if (orderUpdateError) {
        console.error('❌ Error updating order:', orderUpdateError);
      } else {
        console.log('✅ Order updated successfully:', orderUpdateData);
      }

      console.log('📦 Now updating box');

      const { data: boxUpdateData, error: boxUpdateError } = await supabase
        .from('delivery_boxes')
        .update({
          status: 'in_use',
          current_zone: deliveryZone?.zone_name,
          last_used_at: new Date().toISOString()
        })
        .eq('id', selectedBox.id)
        .select();

      if (boxUpdateError) {
        console.error('❌ Error updating box:', boxUpdateError);
      } else {
        console.log('✅ Box updated successfully:', boxUpdateData);
      }

      // Save allocation to state for success screen
      setCompletedAllocation(allocation);
      
      // Show success screen with bay instructions
      setShowSuccessScreen(true);

      // DO NOT auto-close - require manual confirmation

    } catch (err) {
      console.error('Error allocating box:', err);
      setError(err.message || 'Failed to allocate box');
    } finally {
      setAllocating(false);
    }
  };

  const getBayInstruction = (zoneName) => {
    const bayMap = {
      'Laudium': { bay: 'Bay A', color: 'bg-blue-600' },
      'Erasmia': { bay: 'Bay B', color: 'bg-green-600' },
      'Wierda Park': { bay: 'Bay C', color: 'bg-orange-600' },
      'Eldoraigne': { bay: 'Bay C', color: 'bg-orange-600' },
      'Centurion': { bay: 'Bay D', color: 'bg-purple-600' }
    };
    return bayMap[zoneName] || { bay: 'Bay D', color: 'bg-purple-600' };
  };

  const filteredBoxes = boxes.filter(box => {
    if (!box.isAvailable) return false;
    if (searchTerm) {
      return box.box_number.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return true;
  });

  // Success Screen with Bay Instructions
  if (showSuccessScreen) {
    const bayInfo = getBayInstruction(deliveryZone?.zone_name);
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden animate-bounce-in">
          {/* Success Header */}
          <div className={`${bayInfo.color} px-6 py-8 text-white text-center`}>
            <div className="bg-white bg-opacity-20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={48} />
            </div>
            <h2 className="text-3xl font-bold mb-2">Box Allocated!</h2>
            <p className="text-lg opacity-90">Box {selectedBox.box_number}</p>
          </div>

          {/* Bay Instructions */}
          <div className="p-8">
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <Navigation size={32} className="text-yellow-600" />
                <div>
                  <div className="text-sm font-medium text-yellow-800">PLACE BOX IN:</div>
                  <div className="text-3xl font-bold text-yellow-900">{bayInfo.bay}</div>
                </div>
              </div>
              <div className="text-sm text-yellow-800 font-medium">
                Zone: {deliveryZone?.zone_name || 'Detecting...'}
              </div>
            </div>

            {/* Order Details */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Order:</span>
                <span className="font-semibold">{order.order_number}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Box:</span>
                <span className="font-semibold">Box {selectedBox.box_number}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Zone:</span>
                <span className="font-semibold" style={{ color: deliveryZone?.zone_color || '#6B7280' }}>
                  {deliveryZone?.zone_name || 'Detecting...'}
                </span>
              </div>
            </div>

            <button
              onClick={async () => {
                // Update order to confirm box placement
                const { error: confirmError } = await supabase
                  .from('orders')
                  .update({ box_placement_confirmed: true })
                  .eq('id', order.id);
                
                if (confirmError) {
                  console.error('❌ Error confirming box placement:', confirmError);
                } else {
                  console.log('✅ Box placement confirmed for order:', order.id);
                }
                
                if (onAllocated) {
                  onAllocated(completedAllocation, selectedBox);
                }
                onClose();
              }}
              className={`w-full ${bayInfo.color} text-white font-bold py-4 rounded-lg hover:opacity-90 transition-opacity text-lg`}
            >
              ✓ Confirmed - Box Placed in {bayInfo.bay}
            </button>

            <div className="text-center text-sm text-gray-500 mt-4">
              Please confirm you've placed the box in the correct bay
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Modal
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Allocate to Delivery Box</h2>
              <p className="text-green-100 text-sm mt-1">
                Select a box for Order #{order.order_number}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Order Summary with Zone */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="bg-green-100 p-3 rounded-lg">
              <Package size={24} className="text-green-600" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-gray-900">
                Order #{order.order_number}
              </div>
              <div className="text-sm text-gray-600">
                Total: R{order.total?.toFixed(2)} • Status: {order.status}
              </div>
            </div>
            {deliveryZone && (
              <div 
                className="px-4 py-2 rounded-full flex items-center gap-2 text-white font-medium"
                style={{ backgroundColor: deliveryZone.zone_color }}
              >
                <MapPin size={16} />
                {deliveryZone.zone_name}
              </div>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200">
          <input
            type="text"
            placeholder="Search box number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Box Grid */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent"></div>
            </div>
          ) : filteredBoxes.length === 0 ? (
            <div className="text-center py-12">
              <Box size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No available boxes found</p>
              <p className="text-sm text-gray-500 mt-2">
                {searchTerm ? 'Try a different search term' : 'All boxes are currently in use'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
              {filteredBoxes.map((box) => (
                <button
                  key={box.id}
                  onClick={() => setSelectedBox(box)}
                  className={`aspect-square rounded-xl border-2 transition-all transform hover:scale-105 ${
                    selectedBox?.id === box.id
                      ? 'border-green-600 bg-green-50 shadow-lg'
                      : 'border-gray-300 bg-white hover:border-green-400 hover:bg-green-50'
                  }`}
                >
                  <div className="flex flex-col items-center justify-center h-full p-2">
                    <Box
                      size={32}
                      className={
                        selectedBox?.id === box.id
                          ? 'text-green-600'
                          : 'text-gray-600'
                      }
                    />
                    <span
                      className={`text-lg font-bold mt-1 ${
                        selectedBox?.id === box.id
                          ? 'text-green-700'
                          : 'text-gray-900'
                      }`}
                    >
                      {box.box_number}
                    </span>
                    {selectedBox?.id === box.id && (
                      <CheckCircle size={16} className="text-green-600 mt-1" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected Box Info with Bay Preview */}
        {selectedBox && deliveryZone && (
          <div className="px-6 py-3 bg-gradient-to-r from-green-50 to-blue-50 border-t border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Box size={20} className="text-green-600" />
                <span className="text-sm font-medium text-green-900">
                  Box {selectedBox.box_number} → {getBayInstruction(deliveryZone.zone_name).bay}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <MapPin size={14} />
                <span>{deliveryZone.zone_name}</span>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between gap-4">
          <button
            onClick={onClose}
            disabled={allocating}
            className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAllocateBox}
            disabled={!selectedBox || allocating}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {allocating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Allocating...
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                Confirm Box {selectedBox?.box_number}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoxAllocationModal;
